import { useState } from "react";
import { useStores } from "@/stores";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * The two "+ Create new…" dialogs the Templates Linking table opens from its dropdowns.
 * Each owns its own draft name and busy flag and reports only the new row's id, so the
 * table stays about the table.
 */

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new row's id, for the row that opened the dialog. */
  onCreated: (id: number) => void;
}

const failed = (what: string, error: unknown) =>
  toast.error(
    `Failed to create ${what}: ${error instanceof Error ? error.message : String(error)}`
  );

export const CreateCategoryDialog = ({ open, onOpenChange, onCreated }: CreateDialogProps) => {
  const { documentCategoriesStore } = useStores();
  const [name, setName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const close = () => {
    setName("");
    onOpenChange(false);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    try {
      const category = await documentCategoriesStore.createAutoAssignedCategory(name.trim(), null);
      onCreated(category.id);
      toast.success(`Category "${category.category_name}" created`);
      close();
    } catch (error) {
      failed("category", error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Category</DialogTitle>
          <DialogDescription>
            Created as a <code>file-type</code> category, so the LLM will never assign it on its own
            — it is applied only by this template link. Add its Estonian name afterwards in the
            Document Categories → Translations tab.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Covered Works Act"
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!name.trim() || isCreating}>
            {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CreateStageDialog = ({ open, onOpenChange, onCreated }: CreateDialogProps) => {
  const { designStagesStore } = useStores();
  const [name, setName] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const close = () => {
    setName("");
    setNameEn("");
    onOpenChange(false);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    try {
      const stage = await designStagesStore.createStage({
        name: name.trim(),
        name_en: nameEn.trim() || null,
      });
      onCreated(stage.id);
      toast.success(`Design stage "${stage.name}" created`);
      close();
    } catch (error) {
      failed("design stage", error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Design Stage</DialogTitle>
          <DialogDescription>
            The Estonian name is the stage's identity and what gets stored on the document. A new
            stage is <strong>not</strong> selectable by the LLM — the stage classification prompt
            lists its stages literally — so it takes effect only where it is tied to a template.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Name (Estonian)</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Teostusdokumentatsioon"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Name (English, optional)</label>
            <Input
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder="e.g., As-built documentation"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!name.trim() || isCreating}>
            {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
